<script setup lang="ts">
import { useLexicalComposer } from "lexical-vue";
import { computed } from "vue";

import { EVENT_FORMAT_BACKGROUND_COLOR, EVENT_FORMAT_TEXT_COLOR } from "~/components/text/const";
import { ColorHex } from "~/shared/enums";
import { useUserStore } from "~/stores";
import { BACKGROUND_ALPHA } from "~/utils/color";

import ChangeColorDropdownItem from "./ChangeColorDropdownItem.vue";

const userStore = useUserStore();
const editor = useLexicalComposer();

const textColors = Object.values(ColorHex).filter((color) => color !== ColorHex.BRIGHT_RED);
const bgColors = Object.values(ColorHex).filter((color) => color !== ColorHex.BRIGHT_RED);

const recentColors = computed(() => userStore.recentColorCombinedStack.slice(0, 6));

const applyColor = (color: string, isBackground: boolean) => {
  editor.getRootElement()?.focus({ preventScroll: true });
  userStore.addToRecentColorStack(color, isBackground);
  editor.dispatchCommand(
    isBackground ? EVENT_FORMAT_BACKGROUND_COLOR : EVENT_FORMAT_TEXT_COLOR,
    isBackground ? (color ? `${color}${BACKGROUND_ALPHA}` : "") : color
  );
};
</script>

<template>
  <div class="flex flex-col gap-3 px-4 pt-2 pb-3">
    <!-- Recent colors -->
    <div v-if="recentColors.length">
      <span class="mb-1 flex text-xs/normal font-semibold text-vlt uppercase">Recent</span>
      <div class="grid grid-cols-6 gap-2">
        <ChangeColorDropdownItem
          v-for="item in recentColors"
          :key="`${item.isBackground ? 'bg' : 'text'}:${item.color}`"
          :color="item.color"
          :is-background="item.isBackground"
          @click="applyColor" />
      </div>
    </div>

    <!-- Text colors -->
    <div>
      <span class="mb-1 flex text-xs/normal font-semibold text-vlt uppercase">Text</span>
      <div class="grid grid-cols-6 gap-2">
        <ChangeColorDropdownItem color="" :is-background="false" @click="applyColor" />
        <ChangeColorDropdownItem
          v-for="color in textColors"
          :key="color"
          :color="color"
          :is-background="false"
          @click="applyColor" />
      </div>
    </div>

    <!-- Background colors -->
    <div>
      <span class="mb-1 flex text-xs/normal font-semibold text-vlt uppercase">Background</span>
      <div class="grid grid-cols-6 gap-2">
        <ChangeColorDropdownItem color="" is-background @click="applyColor" />
        <ChangeColorDropdownItem
          v-for="color in bgColors"
          :key="color"
          :color="color"
          is-background
          @click="applyColor" />
      </div>
    </div>
  </div>
</template>
